import type { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { Tooltip } from '@mui/material';

import type { LcpnTheme } from '../../styles/theme';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import { useDrawerStateData } from '../../providers/DrawerStateProvider';

import styledUpdateTabSvgUrl from './update-tab.svg';
import starIcon from './star-icon.svg';
import { useVirtualTourConfig } from './VirtualTourProvider';
import { poiTour, heatmapTour } from './shepherd-tours';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  wrapper: {
    position: 'absolute',
    bottom: theme.spacing(4),
    zIndex: 1000,
    height: theme.spacing(8),
    width: theme.spacing(34),
    transition: 'left 400ms ease-in-out',
    left: `calc(50% - ${theme.spacing(17)})`,
  },
  wrapperOpen: {
    left: `calc(${theme.layout.drawerWidth} + (100% - ${
      theme.layout.drawerWidth
    }) / 2 - ${theme.spacing(17)})`,
  },
  wrapperClosed: {
    left: `calc(50% - ${theme.spacing(17)})`,
  },
  [`@media screen and (max-width: ${theme.breakPoints.desktop}px)`]: {
    wrapper: {
      display: 'none',
    },
  },
  tooltipAnchor: {
    position: 'absolute',
    bottom: theme.spacing(1),
    left: 0,
    right: 0,
    margin: '0 auto',
    height: theme.spacing(6),
    width: theme.spacing(4),
    display: 'block',
  },
  extendTourButton: {
    padding: 0,
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      width: theme.spacing(6),
      height: 'auto',
    },
  },
  styledTabImage: {
    position: 'absolute',
    margin: 'auto',
    left: 0,
    right: 0,
    width: '100%',
    height: 'auto',
  },
}));

export const TourUpdateTab: FC = () => {
  const classes = useStyles();
  const { poiTourReady, heatmapTourReady } = useVirtualTourConfig();
  const { selectedExperience } = useNavigationBarData();
  const { poiDrawerOpen, heatmapDrawerOpen } = useDrawerStateData();
  const isOpen =
    selectedExperience === 'heatMap' ? heatmapDrawerOpen : poiDrawerOpen;

  const currentTour = selectedExperience === 'heatMap' ? heatmapTour : poiTour;
  const isCurrentTourReady =
    selectedExperience === 'heatMap' ? heatmapTourReady : poiTourReady;

  return (
    <div
      className={`${classes.wrapper} ${
        isOpen ? classes.wrapperOpen : classes.wrapperClosed
      }`}
    >
      <img
        src={styledUpdateTabSvgUrl}
        alt="updateTab"
        className={classes.styledTabImage}
      />
      <Tooltip title={'Start the Virtual Tour'} arrow>
        <span className={classes.tooltipAnchor}>
          <button
            className={classes.extendTourButton}
            onClick={() => {
              if (!isCurrentTourReady) return;

              // Always start the main tour for the current view
              if (!currentTour.isActive()) {
                currentTour.start();
              }
            }}
            data-tour="tour-update-tab"
            style={{ cursor: isCurrentTourReady ? 'pointer' : 'default' }}
            disabled={!isCurrentTourReady}
          >
            <img src={starIcon} alt="star icon" />
          </button>
        </span>
      </Tooltip>
    </div>
  );
};
